import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import VoiceLogo from "@/components/VoiceLogo";
import axios from "axios";
import { 
  Download, 
  Monitor,
  CheckCircle,
  Smartphone,
  Globe,
  ArrowRight,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function DownloadPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        
        // Fetch the download URL
        try {
          const downloadResponse = await axios.get(`${API}/downloads/windows`);
          if (downloadResponse.data.success && downloadResponse.data.download_url) {
            setDownloadUrl(downloadResponse.data.download_url);
          }
        } catch (dlError) {
          console.error('Could not fetch download URL:', dlError);
          setDownloadError('Download temporarily unavailable');
        }
      } catch (err) {
        // Not authenticated, redirect to auth page
        navigate('/auth');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleDownload = () => {
    if (downloadUrl) {
      setDownloadStarted(true);
      // Build full URL for download
      const fullUrl = downloadUrl.startsWith('http') 
        ? downloadUrl 
        : `${process.env.REACT_APP_BACKEND_URL || ''}${downloadUrl}`;
      window.location.href = fullUrl;
    }
  };

  const handleContinueToApp = () => {
    navigate('/app');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0f1419' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#6366f1' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0f1419' }}>
      {/* Header */}
      <header className="p-4" style={{ borderBottom: '1px solid #2d3748' }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <VoiceLogo size={28} />
            <span className="text-lg font-semibold text-white">Voice Capture</span>
          </div>
          {user && (
            <span className="text-sm" style={{ color: '#a0aec0' }}>
              Welcome, {user.name || user.email}
            </span>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          {/* Success Message */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
              <CheckCircle className="h-10 w-10" style={{ color: '#22c55e' }} />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Account Created!</h1>
            <p className="text-lg" style={{ color: '#a0aec0' }}>
              Choose how you want to use Voice Capture
            </p>
          </div>

          {/* Download Options */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Windows Desktop App */}
            <div 
              className="p-6 rounded-xl border transition-all hover:border-indigo-500"
              style={{ backgroundColor: '#1a2332', borderColor: '#2d3748' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#3b82f620' }}>
                  <Monitor className="h-6 w-6" style={{ color: '#3b82f6' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Windows App</h3>
                  <p className="text-sm" style={{ color: '#a0aec0' }}>Recommended</p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  Global hotkey (Ctrl+Win)
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  Works when minimized
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  Auto-starts with Windows
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  System tray integration
                </li>
              </ul>

              <Button 
                onClick={handleDownload}
                className="w-full py-3 font-semibold"
                style={{ backgroundColor: downloadUrl ? '#3b82f6' : '#4b5563' }}
                data-testid="download-windows-btn"
                disabled={!downloadUrl || downloadStarted}
              >
                <Download className="h-5 w-5 mr-2" />
                {downloadStarted ? 'Download Started!' : downloadUrl ? 'Download for Windows' : 'Preparing Download...'}
              </Button>
              
              {downloadStarted && (
                <p className="text-xs text-center mt-3" style={{ color: '#22c55e' }}>
                  ✓ Check your downloads folder
                </p>
              )}
              
              {downloadError && (
                <p className="text-xs text-center mt-3" style={{ color: '#ef4444' }}>
                  {downloadError}
                </p>
              )}
            </div>

            {/* Web App */}
            <div 
              className="p-6 rounded-xl border transition-all hover:border-indigo-500"
              style={{ backgroundColor: '#1a2332', borderColor: '#2d3748' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#8b5cf620' }}>
                  <Globe className="h-6 w-6" style={{ color: '#8b5cf6' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Web App</h3>
                  <p className="text-sm" style={{ color: '#a0aec0' }}>Use in browser</p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  No download required
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  Works on any device
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  Voice commands supported
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                  <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  Install as PWA (optional)
                </li>
              </ul>

              <Button 
                onClick={handleContinueToApp}
                className="w-full py-3 font-semibold"
                variant="outline"
                style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                data-testid="continue-web-btn"
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                Continue to Web App
              </Button>
            </div>
          </div>

          {/* Mobile Coming Soon */}
          <div 
            className="p-4 rounded-xl text-center"
            style={{ backgroundColor: '#1a2332', border: '1px dashed #2d3748' }}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Smartphone className="h-5 w-5" style={{ color: '#a0aec0' }} />
              <span className="text-sm font-medium" style={{ color: '#a0aec0' }}>
                Android & iOS apps coming soon
              </span>
            </div>
          </div>

          {/* Skip Link */}
          <div className="text-center mt-8">
            <button 
              onClick={handleContinueToApp}
              className="text-sm underline hover:no-underline"
              style={{ color: '#6366f1' }}
            >
              Skip download and go to web app →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
